/**
 * FormField Component
 *
 * A reusable wrapper component for consistent form field styling, error display,
 * help text, and validation states. Provides accessibility features and theme
 * integration for form workflows.
 *
 * @module components/ui/FormField
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';

/**
 * FormField component props interface
 */
export interface FormFieldProps {
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display */
  helperText?: string;
  /** Unique identifier for the field */
  id?: string;
  /** Children components (typically input elements) */
  children: React.ReactNode;
  /** Custom container class */
  className?: string;
  /** Custom label class */
  labelClassName?: string;
  /** Custom message class */
  messageClassName?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Reusable FormField wrapper component with consistent styling and accessibility
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Email Address"
 *   required
 *   error="Please enter a valid email address"
 *   helperText="We'll use this to send you important updates"
 * >
 *   <Input type="email" placeholder="Enter your email" />
 * </FormField>
 * ```
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  required = false,
  error,
  warning,
  success,
  helperText,
  id,
  children,
  className,
  labelClassName,
  messageClassName,
  fullWidth = false,
  disabled = false
}) => {
  const { isDark } = useTheme();
  // Generate unique IDs for accessibility
  const fieldId = id || `field-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = `${fieldId}-error`;
  const warningId = `${fieldId}-warning`;
  const successId = `${fieldId}-success`;
  const helperId = `${fieldId}-helper`;

  // Determine which message to show (priority: error > warning > success > helper)
  const message = error || warning || success || helperText;
  const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;
  const messageType = error ? 'error' : warning ? 'warning' : success ? 'success' : 'helper';

  // Clone children to add accessibility attributes
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        id: fieldId,
        'aria-describedby': message ? messageId : undefined,
        'aria-invalid': error ? 'true' : 'false',
        'aria-required': required,
        state: error ? 'error' : warning ? 'warning' : success ? 'success' : 'default'
      });
    }
    return child;
  });

  return (
    <div className={cn(
      'space-y-1.5',
      fullWidth && 'w-full',
      disabled && 'opacity-60 pointer-events-none',
      className
    )}>
      {/* Label */}
      {label && (
        <label
          htmlFor={fieldId}
          className={cn(
            'block text-sm font-medium transition-colors duration-200',
            isDark ? 'text-secondary-300' : 'text-secondary-700',
            required && 'after:content-["*"] after:ml-0.5 after:text-red-500',
            disabled && 'text-secondary-500',
            labelClassName
          )}
        >
          {label}
        </label>
      )}

      {/* Input/Children Container */}
      <div className="relative">
        {enhancedChildren}
      </div>

      {/* Message */}
      {message && (
        <div
          id={messageId}
          data-testid={error ? `${fieldId}-error` : warning ? `${fieldId}-warning` : success ? `${fieldId}-success` : `${fieldId}-helper`}
          className={cn(
            'text-sm leading-5 transition-colors duration-200',
            messageType === 'error' && (isDark ? 'text-red-400' : 'text-red-600'),
            messageType === 'warning' && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
            messageType === 'success' && (isDark ? 'text-green-400' : 'text-green-600'),
            messageType === 'helper' && (isDark ? 'text-secondary-400' : 'text-secondary-600'),
            messageClassName
          )}
          role={error ? 'alert' : undefined}
          aria-live={error ? 'polite' : undefined}
        >
          {/* Icon for different message types */}
          <span className="inline-flex items-start gap-1.5">
            {messageType === 'error' && (
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {messageType === 'warning' && (
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {messageType === 'success' && (
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {messageType === 'helper' && (
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className="flex-1">{message}</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default FormField;